/**
 * Self-service profile + 2FA for the signed-in dashboard user.
 *
 * Identity is owned by the upstream support-center (portal-api); this
 * page proxies through the dashboard server (`/api/profile*` →
 * portal-api `/api/v1/profile` + `/api/v1/auth/2fa/*`) as the acting
 * user. Email is the SSO login identity and is intentionally NOT
 * editable (portal-api `profile_params` does not permit it).
 *
 * This file is the orchestrator: it loads the profile and composes the
 * cards. Each card lives in `profile-page/` (AvatarCard / IdentityCard /
 * ChangePasswordCard / TwoFactorCard); the shared `ProfileUser` shape +
 * `unwrap`/`errMsg` helpers live in `profile-page/profileTypes`.
 */
import { Box, Button, Typography } from "@mui/material";
import { useQuery, useQueryClient } from "@tanstack/react-query";

import { PageLoader } from "@/components/ui/page-loader";
import { api } from "../../lib/api.js";
import { AboutCard } from "./AboutCard.js";
import { AvatarCard } from "./profile-page/AvatarCard.js";
import { ChangePasswordCard } from "./profile-page/ChangePasswordCard.js";
import { IdentityCard } from "./profile-page/IdentityCard.js";
import { unwrap } from "./profile-page/profileTypes.js";
import { TwoFactorCard } from "./profile-page/TwoFactorCard.js";

export default function ProfilePage(): JSX.Element {
  const qc = useQueryClient();
  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: ["profile"],
    queryFn: async () => unwrap(await api<unknown>("/api/profile")),
  });

  if (isLoading) {
    return <PageLoader />;
  }
  if (isError || !data) {
    return (
      <Box sx={{ mx: "auto", maxWidth: "42rem", p: 3 }}>
        <Typography sx={{ fontSize: 14, color: "error.main" }}>
          Could not load your profile.
        </Typography>
        <Button sx={{ mt: 1.5 }} onClick={() => refetch()}>
          Retry
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ mx: "auto", maxWidth: "42rem", display: "grid", gap: 3, p: 3 }}>
      <Box component="header">
        <Typography component="h1" sx={{ fontSize: "1.25rem", fontWeight: 600, color: "text.primary" }}>
          Your profile
        </Typography>
        <Typography sx={{ mt: 0.5, fontSize: 14, color: "text.secondary" }}>
          Manage your name, password, and two-factor authentication.
        </Typography>
      </Box>

      <AvatarCard
        user={data}
        onSaved={() => qc.invalidateQueries({ queryKey: ["profile"] })}
      />
      <IdentityCard
        user={data}
        onSaved={() => qc.invalidateQueries({ queryKey: ["profile"] })}
      />
      <ChangePasswordCard />
      <TwoFactorCard
        user={data}
        onChanged={() => qc.invalidateQueries({ queryKey: ["profile"] })}
      />
      {/* Desktop-only "About this app" + post-upgrade "what's new" deep-link
          (renders nothing on the web). */}
      <AboutCard />
    </Box>
  );
}
